import React, { useState } from 'react';
import { IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Button, Box, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

/**
 * Formats scientific notation values to decimal format with maximum 4 decimal places
 * @param value - String value that might be in scientific notation (e.g., "5.00E-03")
 * @returns Formatted decimal string (e.g., "0.01", "0.0005") or original value if not scientific notation
 */
const formatScientificNotation = (value: string | number): string => {
    if (typeof value !== 'string') {
        return String(value);
    }

    // Check if the value is in scientific notation format (e.g., "5.00E-03", "1.00E-04")
    const scientificPattern = /^([+-]?[\d.]+)[eE]([+-]?\d+)$/;
    const match = value.match(scientificPattern);

    if (match) {
        // Convert to number, then format with maximum 4 decimal places
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            // Format with maximum 4 decimal places, then remove trailing zeros
            const finalValue = Math.round(numericValue * 10000) / 10000;
            return finalValue.toString();
        }
    }

    return value;
};

/** Table data structure for InfoBox table display */
interface InfoBoxTableData {
    /** Column headers */
    headers: string[];
    /** Table rows - each row is an array of cell values */
    rows: (string | number)[][];
    /** Optional table caption */
    caption?: string;
}

interface InfoBoxButtonProps {
    /** Source URL or path to the image to display */
    imageSrc?: string;
    /** Table data to display instead of image */
    tableData?: InfoBoxTableData;
    /** Optional tooltip text shown on hover */
    tooltipText?: string | null;
    /** Optional text content to display in the dialog (for text-only mode) */
    textContent?: string | null;
    /** Optional dialog title - defaults to "Information" */
    dialogTitle?: string | null;
    /** Optional alt text for the image - defaults to "Information image" */
    imageAlt?: string | null;
    /** Size of the info button - defaults to "medium" */
    size?: 'small' | 'medium' | 'large';
}

/**
 * InfoBoxButton component that displays an info icon next to form fields.
 * When clicked, opens a modal dialog with an image, table, or text display.
 *
 * Features:
 * - Hover tooltip for quick info
 * - Click to open image, table, or text in modal dialog
 * - Responsive display for all content types
 * - Accessible with ARIA labels
 * - Material-UI themed
 *
 * @param imageSrc - URL for image content
 * @param tableData - Structured data for table content
 * @param textContent - Plain text content for text-only mode
 * @param tooltipText - Hover tooltip text
 * @param dialogTitle - Modal dialog title
 * @param imageAlt - Alt text for images
 * @param size - Button size
 */
const InfoBoxButton: React.FC<InfoBoxButtonProps> = ({ imageSrc, tableData, textContent, tooltipText, imageAlt = 'Info image', dialogTitle, size = 'small' }) => {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Validation: ensure at least one content type is provided
    if (!imageSrc && !tableData && !textContent) {
        return null;
    }

    const isTableMode = !!tableData;
    const isTextMode = !!textContent && !imageSrc && !tableData;

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleImageError = () => {
        setImageError(true);
    };

    // Default values with fallbacks for translations
    const defaultTooltip = isTableMode 
        ? t('info_box_tooltip_table', 'Click to view data table') 
        : isTextMode 
        ? (tooltipText || t('info_box_tooltip_text', 'Click for more information'))
        : t('info_box_tooltip', 'Click to view additional information');
    const tooltip = isTextMode ? defaultTooltip : (tooltipText || defaultTooltip);
    const ariaLabel = t('info_box_aria_label', 'Show additional information');

    return (
        <>
            <Tooltip title={tooltip} placement="top" arrow>
                <IconButton
                    onClick={handleOpenDialog}
                    size={size}
                    aria-label={ariaLabel}
                    sx={{
                        ml: 0.5,
                        color: 'primary.main',
                        '&:hover': {
                            color: 'primary.dark',
                            backgroundColor: 'action.hover',
                        },
                    }}>
                    <InfoIcon fontSize={size} />
                </IconButton>
            </Tooltip>

            <Dialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                maxWidth={isTableMode ? 'lg' : isTextMode ? 'sm' : 'md'}
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '90vh',
                    },
                }}
                aria-labelledby="info-dialog-title"
                aria-describedby="info-dialog-content">
                <DialogTitle
                    id="info-dialog-title"
                    sx={{
                        display: 'flex',
                        justifyContent: isTableMode && tableData?.caption ? 'flex-end' : 'space-between',
                        alignItems: 'center',
                        pb: 1,
                    }}>
                    {/* Hide title text for table mode with caption - caption serves as title */}
                    {!(isTableMode && tableData?.caption) && (dialogTitle || t('info_box_dialog_title', 'Additional Information'))}
                    <IconButton aria-label={t('close', 'Close') || 'Close'} onClick={handleCloseDialog} sx={{ color: 'grey.500' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    id="info-dialog-content"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: isTableMode ? 'flex-start' : 'center',
                        p: 2,
                    }}>
                    {isTextMode ? (
                        // Text content
                        <Box sx={{ width: '100%', p: 2 }}>
                            <Box sx={{ color: 'text.primary', fontSize: '1rem', lineHeight: 1.6 }}>
                                {textContent}
                            </Box>
                        </Box>
                    ) : isTableMode ? (
                        // Table content
                        <Box sx={{ width: '100%' }}>
                            {tableData!.caption && <Box sx={{ mb: 2, textAlign: 'center', fontWeight: 'medium', color: 'text.secondary' }}>{tableData!.caption}</Box>}
                            <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                                <Table size="small" aria-label="info data table">
                                    <TableHead>
                                        <TableRow>
                                            {tableData!.headers.map((header, index) => (
                                                <TableCell
                                                    key={index}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: 'grey.50',
                                                        ...(index === 0 && { minWidth: '120px' }),
                                                    }}>
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData!.rows.map((row, rowIndex) => (
                                            <TableRow
                                                key={rowIndex}
                                                sx={{
                                                    '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                                                    '&:hover': { backgroundColor: 'action.selected' },
                                                }}>
                                                {row.map((cell, cellIndex) => {
                                                    const isLastColumn = cellIndex === row.length - 1;
                                                    const displayValue = isLastColumn ? formatScientificNotation(cell) : cell;

                                                    return (
                                                        <TableCell
                                                            key={cellIndex}
                                                            component={cellIndex === 0 ? 'th' : 'td'}
                                                            scope={cellIndex === 0 ? 'row' : undefined}
                                                            sx={{
                                                                ...(cellIndex === 0 && { fontWeight: 'medium' }),
                                                                ...(cellIndex > 0 && { textAlign: 'right' }),
                                                            }}>
                                                            {displayValue}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        // Image content (existing logic)
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                minHeight: '200px',
                            }}>
                            {!imageError ? (
                                <img
                                    src={imageSrc!}
                                    alt={imageAlt || undefined}
                                    onError={handleImageError}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 4,
                                        color: 'text.secondary',
                                    }}>
                                    <InfoIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                                    <span>{t('info_box_image_error', 'Unable to load image')}</span>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} variant="contained" color="primary">
                        {t('close', 'Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InfoBoxButton;
